import React from "react";
import cn from "classnames";

import { User } from "@/types";

import AppTable from "@/components/AppTable";
import AppTableContainer from "@/components/AppTableContainer";
import AppTableHead from "@/components/AppTable/AppTableHead";
import AppTableBody from "@/components/AppTable/AppTableBody";
import AppTableRow from "@/components/AppTable/AppTableRow";
import AppTableCell from "@/components/AppTable/AppTableCell";
import AppTableHeaderCell from "@/components/AppTable/AppTableHeaderCell";
import AppBox from "@/components/AppBox";
import AppText from "@/components/AppText";
import AppTooltip from "@/components/AppTooltip";
import AppSwitch from "@/components/AppSwitch";
import AppSwitchInput from "@/components/AppSwitchInput";
import AppIcon from "@/components/AppIcon";

interface AdminUsersTableProps {
  tableRows: User[];
  onDelete: (row: User) => void;
  onLoginUnderUser: (row: User) => void;
  onChangeCreateAgency: (row: User) => void;
  onClickReport: (row: User) => void;
  onChangeSetting: (userId: number, field: string, currentValue: boolean) => void;
  onChangeGroupText: (row: User, phone: string) => void;
  isCanceled?: boolean;
  className?: string;
}

const getIntegrationLogo = (name: string) => {
  switch (name) {
    case 'Service Fusion':
      return '/images/logo_service_fusion.png'
    case 'Workiz':
      return '/images/logo_workiz.png'
    case 'Jobber':
      return '/images/logo_jobber.png'
    default:
      return undefined
  }
}

const AdminUsersTable: React.FC<AdminUsersTableProps> = ({
  tableRows,
  onDelete,
  onLoginUnderUser,
  onChangeCreateAgency,
  onClickReport,
  onChangeSetting,
  onChangeGroupText,
  isCanceled = false,
  className = "",
}) => {
  return (
    <AppTableContainer className={className}>
      <AppTable className="table-fixed">
        {/* Conditional Sticky Header */}
        <AppTableHead>
          <AppTableRow>
            <AppTableHeaderCell>Name</AppTableHeaderCell>
            <AppTableHeaderCell>Customer ID</AppTableHeaderCell>
            <AppTableHeaderCell>Email</AppTableHeaderCell>
            <AppTableHeaderCell>Agency Name</AppTableHeaderCell>
            <AppTableHeaderCell align="center">Subscription</AppTableHeaderCell>
            {!isCanceled && <AppTableHeaderCell align="center">Allow access</AppTableHeaderCell>}
            {!isCanceled && <AppTableHeaderCell align="center">Agency</AppTableHeaderCell>}
            {!isCanceled && <AppTableHeaderCell align="center">Features</AppTableHeaderCell>}
            <AppTableHeaderCell align="center">Actions</AppTableHeaderCell>
          </AppTableRow>
        </AppTableHead>

        <AppTableBody>
          {tableRows.length ? (
            tableRows
              .sort(
                (a, b) =>
                  new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              )
              .map((row, index) => {
                const isUserCanceled = row.isSubscribed && !row.isCanceled && !row.deleted_at
                return (
                  <AppTableRow key={index} className="hover:bg-gray-50 h-[151px]" style={{ height: '150px' }}>
                    {/* Name and Integrations */}
                    <AppTableCell className="h-[151px]">
                      <AppBox column gap={1} width={200}>
                        <AppText>{row.name}</AppText>
                        {row.integrations.map((integration, idx) => {
                          const imgSrc = getIntegrationLogo(integration.name)
                          return (
                            <AppBox key={idx} align="center" gap={1}>
                              <AppTooltip
                                title={`${integration.name} ${integration.name === "Service Fusion" && integration.pro ? "(PRO)" : ""}`}>
                                <img
                                  src={imgSrc}
                                  className={cn(
                                    "w-4 h-4",
                                    !integration.pro && "opacity-50"
                                  )}
                                  alt={integration.name}
                                />
                              </AppTooltip>
                            </AppBox>
                          )
                        })}
                      </AppBox>
                    </AppTableCell>

                    {/* Customer ID */}
                    <AppTableCell className="h-[151px]">
                      <AppText>
                        {row.customer_id}
                      </AppText>
                    </AppTableCell>

                    {/* Email */}
                    <AppTableCell className="h-[151px]">
                      <AppText>
                        {row.email}
                      </AppText>
                    </AppTableCell>

                    {/* Agency Name */}
                    <AppTableCell className="h-[151px]">
                      <AppText>
                        {row.agency?.name ?? ""}
                      </AppText>
                    </AppTableCell>

                    {/* Subscription */}
                    <AppTableCell className="h-[151px]" align="center">
                      {isUserCanceled ? (
                        <AppIcon name="checkCircle" className="text-primary" />
                      ) : (
                        <AppIcon name="xCircle" className="text-error" />
                      )}
                    </AppTableCell>

                    {/* Allow Access */}
                    {
                      ! isCanceled &&
                        <AppTableCell className="h-[151px]" align="center">
                          <AppSwitch
                            checked={row.is_live}
                            onChange={() => onChangeSetting(row.id, "is_live", row.is_live)}
                          />
                        </AppTableCell>
                    }

                    {/* Agency */}
                    {
                      ! isCanceled &&
                        <AppTableCell className="h-[151px]" align="center">
                          <AppSwitch
                            checked={!!row.is_agency_admin}
                            disabled={!row.is_agency_admin && !!row.agency_id}
                            onChange={() => onChangeCreateAgency(row)}
                          />
                        </AppTableCell>
                    }
                    

                    {/* Features */}
                    {
                      ! isCanceled &&
                        <AppTableCell className="h-[151px]">
                          <AppBox fullWidth justify="center">
                            <AppBox column gap={2}>
                              <AppSwitch
                                checked={row.gls}
                                label="Messages"
                                labelPosition="right"
                                onChange={() => onChangeSetting(row.id, "gls", row.gls)}
                              />
                              <AppSwitch
                                checked={row.sms}
                                label="Texts"
                                labelPosition="right"
                                onChange={() => onChangeSetting(row.id, "sms", row.sms)}
                              />
                              {/* <AppSwitch
                                checked={row.download_audio}
                                label="Download Audio"
                                labelPosition="right"
                                onChange={() =>
                                  onChangeSetting(row.id, "download_audio", row.download_audio)
                                }
                              /> */}
                              <AppSwitchInput
                                label="Group Texts"
                                labelPosition="right"
                                inputPlaceholder="Phone number"
                                checked={!!(row.group_text && row.phones?.length)}
                                onChange={(phone: any) => onChangeGroupText(row, phone)}
                                showInput={!row.phones?.length}
                              />
                              <AppSwitch
                                checked={row.lead_auto_email}
                                label="Lead Auto-Email to User"
                                labelPosition="right"
                                onChange={() =>
                                  onChangeSetting(row.id, "lead_auto_email", row.lead_auto_email)
                                }
                              />
                              {/* <AppSwitch
                                checked={row.after_hours_enabled}
                                label="After Hours Message"
                                labelPosition="right"
                                onChange={() =>
                                  onChangeSetting(row.id, "after_hours_enabled", row.after_hours_enabled)
                                }
                              /> */}
                            </AppBox>
                          </AppBox>
                        </AppTableCell>
                    }


                    {/* Actions */}
                    <AppTableCell className="h-[151px]">
                      <AppBox center gap={2}>
                        <AppTooltip title="Login As User">
                          <AppIcon
                            name="login"
                            className="text-primary hover:text-primary-hover cursor-pointer"
                            onClick={() => onLoginUnderUser(row)}
                          />
                        </AppTooltip>

                        {
                          !isCanceled &&
                          <>
                            <AppTooltip title="View Report">
                              <AppIcon
                                name="chartBar"
                                className="text-secondary hover:text-secodnary-hover cursor-pointer"
                                onClick={() => onClickReport(row)}
                              />
                            </AppTooltip>

                            <AppTooltip title="Delete User">
                              <AppIcon
                                name="trash"
                                className="text-error hover:text-error-hover cursor-pointer"
                                onClick={() => onDelete(row)}
                              />
                            </AppTooltip>
                          </>
                        }
                      </AppBox>
                    </AppTableCell>
                  </AppTableRow>
                )
              })
          ) : (
            <AppTableRow>
              <AppTableCell colSpan={9} className="text-center">
                No results
              </AppTableCell>
            </AppTableRow>
          )}
        </AppTableBody>
      </AppTable>
    </AppTableContainer>
  );
};

export default AdminUsersTable;